import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { ArrowRight, CheckCheck, Palmtree, Timer, TrendingDown, TrendingUp, Users } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { AreaChart, ChartKey, type Series } from "@/components/data/AreaChart";
import { FigureTile } from "@/components/data/FigureTile";
import { PageHeader } from "@/components/ui/PageHeader";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import { Blocks, type Block } from "@/features/dashboard/Blocks";
import { Inbox } from "@/features/dashboard/Inbox";
import { TodayFaces, TodayPanel, type TodayView } from "@/features/dashboard/WhoIsIn";
import {
  useDashboard,
  useToday,
  type BranchStaffing,
  type Dashboard,
  type ExpiringDocument,
  type OvertimePoint,
  type PendingTask,
  type StaffingPoint,
  type UnderstaffedDay,
} from "@/features/dashboard/hooks";

/**
 * The HR home page.
 *
 * Only things waiting on a person. A count of zero is still shown, because
 * "nothing to approve" is information; anything with a count links straight
 * to the queue that clears it.
 */
export function DashboardRoute() {
  const { data, isLoading } = useDashboard();

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-36 w-full" />
          ))}
        </div>
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const expiring = data?.expiring_documents ?? [];
  // One row per person, not per document. Somebody whose Emirates ID, visa,
  // passport and contract all lapsed on the same day is one conversation, and
  // as four rows they crowded out three other people entirely.
  const expired = groupByPerson(expiring.filter((item) => item.days_left < 0));
  const soon = groupByPerson(expiring.filter((item) => item.days_left >= 0));

  const tasks = data?.tasks ?? [];
  const probation = data?.probation_ending ?? [];
  const understaffed = data?.understaffed_days ?? [];
  const hasTrends =
    (data?.staffing_trend ?? []).length > 0 || (data?.overtime_trend ?? []).length > 0;

  // The sections of the page, in the order it ships with. Each person can put
  // them in their own; the ids are what their order is stored in, so they do
  // not change. The two charts sit side by side and move as one block, and so
  // do papers and branches: taken apart they would each need the full width,
  // and the page would be half as much again to scroll.
  const blocks: Block[] = [
    {
      // The queue first. It is the reason HR opens this page, and it sat
      // below two charts; the figures and the pictures follow it now.
      id: "waiting",
      label: "Waiting on you",
      // With nothing waiting it is a panel without a heading, and so without
      // a grip.
      bare: tasks.length === 0,
      render: (menu) => <TaskList tasks={tasks} menu={menu} />,
    },
    {
      id: "figures",
      label: "Today's figures",
      bare: true,
      render: () => <StatsRow data={data} />,
    },
    {
      id: "charts",
      label: "Cover and overtime",
      shown: hasTrends,
      render: (menu) => <Trends data={data} menu={menu} />,
    },
    {
      id: "probation",
      label: "Probation ending",
      shown: probation.length > 0,
      render: (menu) => (
        <Card
          title="Probation ending"
          description="Confirm the appointment or end it before the date passes."
          grip
          actions={menu}
        >
          <ul className="divide-y divide-line-faint">
            {probation.map((person) => (
              <li
                key={person.user_id}
                className="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <Link
                    to={`/admin/employees/${person.user_id}`}
                    className="font-medium text-fg hover:text-accent hover:underline"
                  >
                    {person.employee_name}
                  </Link>
                  <p className="mt-0.5 text-xs text-fg-subtle">
                    {person.position_title ?? "No position recorded"}
                    {person.date_of_joining
                      ? ` · joined ${formatDate(person.date_of_joining)}`
                      : ""}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p
                    className={cn(
                      "text-base font-medium",
                      person.days_left <= 7 ? "text-alert-600" : "text-ochre-700",
                    )}
                  >
                    {person.days_left < 0
                      ? `ended ${Math.abs(person.days_left)}d ago`
                      : person.days_left === 0
                        ? "ends today"
                        : `${person.days_left}d left`}
                  </p>
                  <p className="text-xs text-fg-subtle">{formatDate(person.probation_ends)}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ),
    },
    {
      id: "papers-branches",
      label: "Papers and branches",
      render: (menu) => (
        <div className="grid items-start gap-5 lg:grid-cols-2">
          <Card
            title={expired.length > 0 ? "Papers out of date" : "Papers falling due"}
            description={
              expired.length > 0
                ? "Expired first. An employee working on a lapsed visa is the company's problem, not a diary entry."
                : "Emirates ID, passport, visa and contract dates in the next 90 days."
            }
            flush={expiring.length === 0}
            grip
            actions={menu}
          >
            {expiring.length === 0 ? (
              <EmptyState
                title="Everything is in date"
                description="You will see anything within 90 days here."
              />
            ) : (
              <div className="space-y-5">
                {expired.length > 0 ? (
                  <PeopleWithPapers rows={expired.slice(0, 6)} total={expired.length} expired />
                ) : null}
                {soon.length > 0 ? (
                  <PeopleWithPapers
                    rows={soon.slice(0, expired.length > 0 ? 4 : 8)}
                    total={soon.length}
                  />
                ) : null}
              </div>
            )}
          </Card>

          <Card
            title="Branches below minimum"
            description="The next two weeks."
            flush={understaffed.length === 0}
            grip
            actions={menu}
          >
            {understaffed.length === 0 ? (
              <EmptyState
                icon={<CheckCheck className="size-5" />}
                title="Every branch is covered"
                description="Arrange cover from the roster if that changes."
              />
            ) : (
              <ShortStrip days={understaffed} />
            )}
          </Card>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Today" />
      <Blocks blocks={blocks} storageKey="hrsys.dashboard.order" />
    </div>
  );
}

interface PersonPapers {
  user_id: string;
  /** Their record, or the library for the company's own documents. */
  link: string;
  employee_name: string;
  documents: string[];
  worst_days: number;
  worst_date: string;
}

/** One row per person, carrying whichever of their papers is furthest gone. */
function groupByPerson(rows: ExpiringDocument[]): PersonPapers[] {
  const people = new Map<string, PersonPapers>();
  for (const row of rows) {
    const key = row.user_id ?? "company";
    const held = people.get(key);
    if (held) {
      held.documents.push(row.document);
      if (row.days_left < held.worst_days) {
        held.worst_days = row.days_left;
        held.worst_date = row.expires_on;
      }
    } else {
      people.set(key, {
        user_id: key,
        link: row.user_id ? `/admin/employees/${row.user_id}` : "/admin/library",
        employee_name: row.employee_name,
        documents: [row.document],
        worst_days: row.days_left,
        worst_date: row.expires_on,
      });
    }
  }
  return [...people.values()].sort((a, b) => a.worst_days - b.worst_days);
}

/**
 * People whose papers need doing something about.
 *
 * The colour sits on the person, once, rather than on every line. A column of
 * ten identical red strings is a column with no emphasis left in it.
 */
function PeopleWithPapers({
  rows,
  total,
  expired = false,
}: {
  rows: PersonPapers[];
  total: number;
  expired?: boolean;
}) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="u-label">{expired ? "Expired" : "Falling due"}</p>
        {/* It used to say "showing 6 of 23" and stop there, which left the
            other seventeen unreachable from this screen; then it opened the
            list of every employee, which was not much better. It opens the
            papers themselves now: which one, whose, and how late. */}
        <Link
          to={expired ? "/admin/papers?state=expired" : "/admin/papers?state=due"}
          className="shrink-0 text-xs font-semibold text-accent hover:underline"
        >
          {total > rows.length ? `${rows.length} of ${total} — see all` : "See the list"}
        </Link>
      </div>
      <ul className="divide-y divide-line-faint">
        {rows.map((person) => (
          <li
            key={person.user_id}
            className="flex items-center justify-between gap-3 py-2.5 first:pt-0"
          >
            <div className="min-w-0">
              <Link
                to={person.link}
                className="truncate text-base font-medium text-fg hover:text-accent hover:underline"
              >
                {person.employee_name}
              </Link>
              <p className="truncate text-xs text-fg-subtle">{person.documents.join(", ")}</p>
            </div>
            <div className="shrink-0 text-right">
              <p
                className={cn(
                  "text-base font-medium tabular-nums",
                  expired ? "text-alert-600" : person.worst_days <= 30 ? "text-ochre-700" : "text-fg-muted",
                )}
              >
                {expired
                  ? `${Math.abs(person.worst_days)}d overdue`
                  : `${person.worst_days}d left`}
              </p>
              <p className="text-xs text-fg-subtle">{formatDate(person.worst_date)}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The fortnight around today: what the branches asked for against who is
 * actually on the rota.
 *
 * Four figures tell you about this morning. They cannot tell you whether this
 * morning is ordinary, and that is the question somebody opening a dashboard
 * is really asking.
 */
function Trends({ data, menu }: { data: Dashboard | undefined; menu: ReactNode }) {
  const points = data?.staffing_trend ?? [];
  const weeks = data?.overtime_trend ?? [];
  if (points.length === 0 && weeks.length === 0) return null;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {points.length > 0 ? (
        <StaffingChart points={points} byBranch={data?.staffing_by_branch ?? []} menu={menu} />
      ) : null}
      {weeks.length > 0 ? <OvertimeChart weeks={weeks} menu={menu} /> : null}
    </div>
  );
}

function StaffingChart({
  points: company,
  byBranch,
  menu,
}: {
  points: StaffingPoint[];
  byBranch: BranchStaffing[];
  /** The pair's move menu. The two charts move as one block. */
  menu: ReactNode;
}) {
  const today = new Date().toISOString().slice(0, 10);
  // A company total that looks fine can be one branch over and another
  // short, so the chart can be read for one branch at a time.
  const [branchId, setBranchId] = useState("");
  const points = byBranch.find((branch) => branch.branch_id === branchId)?.points ?? company;
  // Both lines, not one line and a rule. The shortfall is the gap between
  // them, and a gap is something you can see; a dashed line at a single
  // number taken from the first point was only ever a stand-in for it.
  const series: Series[] = [
    {
      label: "On the rota",
      values: points.map((point) => point.rostered),
      colour: "brand",
      fill: true,
    },
    {
      label: "Asked for",
      values: points.map((point) => point.required),
      colour: "ink",
    },
  ];

  return (
    <Card
      title="Cover, two weeks"
      description="People on the rota each day against the places asked for. Below the grey line, the rota is short."
      help={
        <>
          <p>
            The shaded line is how many people are on the rota each day. The grey line is how
            many places the branches asked for in Settings, Staffing needs.
          </p>
          <p>
            Where the shaded line falls below the grey one, the rota is short that day. Last
            week is on the left, next week on the right, and today is marked.
          </p>
          <p>Choose a branch to see its own fortnight rather than the company total.</p>
        </>
      }
      grip
      actions={
        <>
          {byBranch.length > 1 ? (
            <Select
              dense
              aria-label="Branch"
              placeholder="Every branch"
              className="w-44"
              options={byBranch.map((branch) => ({
                value: branch.branch_id,
                label: branch.branch_name,
              }))}
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
            />
          ) : null}
          {menu}
        </>
      }
    >
      <div className="mb-3">
        <ChartKey series={series} />
      </div>
      <AreaChart
        gridlines
        labels={points.map((point) =>
          point.date === today ? "Today" : formatDate(point.date).replace(/ \d{4}$/, ""),
        )}
        series={series}
        format={(value) => `${value} staff`}
      />
    </Card>
  );
}

/**
 * Overtime, week by week.
 *
 * The figures for this have been computed, serialised and sent with every
 * dashboard response since the endpoint was written — the comment beside them
 * in the API says "so a spike has something to be a spike against" — and
 * nothing has ever drawn them. This is that.
 */
function OvertimeChart({ weeks, menu }: { weeks: OvertimePoint[]; menu: ReactNode }) {
  const series: Series[] = [
    {
      label: "Hours",
      values: weeks.map((week) => week.hours),
      colour: "ochre",
      fill: true,
    },
  ];
  const average =
    weeks.reduce((total, week) => total + week.hours, 0) / Math.max(1, weeks.length);

  return (
    <Card
      title="Overtime, eight weeks"
      description="Overtime hours across everyone, week by week. The dashed line is the average."
      help={
        <>
          <p>
            Every hour of overtime the attendance import has recorded, added up per week, for
            the last eight weeks. The dashed line is the average of those eight.
          </p>
          <p>
            A week well above the line is worth asking about: a branch short of people, or a
            badge that is being punched for somebody else.
          </p>
        </>
      }
      grip
      actions={menu}
    >
      <div className="mb-3">
        <ChartKey series={series} marker={average > 0 ? { label: "Average" } : undefined} />
      </div>
      <AreaChart
        gridlines
        labels={weeks.map((week) => formatDate(week.week_start).replace(/ \d{4}$/, ""))}
        series={series}
        marker={average > 0 ? { value: average, label: "Average" } : undefined}
        format={(value) => `${value.toFixed(1)} h`}
      />
    </Card>
  );
}

/**
 * The four figures that describe the day before anything is asked of anyone.
 *
 * Who is here, who is away, what the overtime bill looks like so far, and
 * which way the headcount has moved.
 *
 * Three of the four are ink. Only a figure that is telling HR something takes
 * a colour: attendance goes red when a third of the staff are missing, and
 * the headcount is green or red according to which way it moved. Colouring
 * all four made the palette decorative, and a decorative palette cannot warn
 * anybody about anything.
 */
function StatsRow({ data }: { data: Dashboard | undefined }) {
  const change = data?.headcount_change_30d ?? 0;
  const rising = change >= 0;
  const today = useToday();
  const [panel, setPanel] = useState<TodayView | null>(null);
  // The list and the figure come from the same reckoning on the server; the
  // list wins once it is here, so the tile never says a number its own
  // panel then contradicts.
  const onDuty = today.data?.on_duty.length ?? data?.on_duty_today ?? 0;
  const onLeave = today.data?.on_leave.length ?? data?.on_leave_today ?? 0;
  const restDay = today.data?.rest_day.length ?? data?.rest_day_today ?? 0;
  const notScheduled = today.data?.not_scheduled.length ?? data?.not_scheduled_today ?? 0;
  const active = data?.active_employees ?? 0;
  const thin = active > 0 && onDuty / active < 0.67;
  const rostered = (data?.staffing_trend ?? []).map((point) => point.rostered);
  const overtimeWeeks = (data?.overtime_trend ?? []).map((point) => point.hours);

  return (
    <div className="grid gap-4 lg:grid-cols-4">
      {/* The one tinted tile, and the first thing HR wants off this screen.
          A row of four identical white cards gives the eye nowhere to land —
          which is what this row was until now, despite a comment saying
          otherwise. */}
      <FigureTile
        tone="board"
        label="On duty today"
        value={String(onDuty)}
        unit={`of ${active}`}
        help="How many people are at work today, out of everybody active: those on today's rota, and, where a branch has no rota for today or somebody is not on a branch rota, those whose record has them working. Suspended accounts, leavers and anybody on approved leave are not counted. See who opens the names. The small line under it is the number rostered day by day."
        icon={<Users className="size-4" />}
        trend={
          rostered.length > 1
            ? {
                values: rostered,
                label: "People rostered, day by day",
                format: (value: number) => `${value} staff`,
              }
            : undefined
        }
        caption={
          <>
            {thin ? "Thin on the ground. " : ""}
            {onLeave} on leave, {restDay} on a day off
            {notScheduled > 0 ? `, ${notScheduled} not scheduled` : ""}.
          </>
        }
        footer={<TodayFaces board={today.data} onOpen={() => setPanel("on_duty")} />}
      />
      <TodayPanel
        open={panel !== null}
        onOpenChange={(open) => setPanel(open ? (panel ?? "on_duty") : null)}
        board={today.data}
        loading={today.isLoading}
        view={panel ?? "on_duty"}
        onViewChange={setPanel}
      />
      <FigureTile
        label="On leave today"
        value={String(onLeave)}
        unit="staff"
        help="Everybody whose approved leave covers today. Requests still waiting for a decision are not counted."
        icon={<Palmtree className="size-4" />}
        caption="Approved leave."
        footer={
          onLeave > 0 ? (
            <button
              type="button"
              onClick={() => setPanel("on_leave")}
              className="-mb-1 flex items-center gap-1 self-start rounded-sm text-xs font-semibold text-accent outline-none hover:underline focus-visible:ring-2 focus-visible:ring-accent"
            >
              See who
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </button>
          ) : null
        }
      />
      <FigureTile
        label="Overtime this month"
        value={(data?.overtime_hours_month ?? 0).toFixed(1)}
        unit="h"
        help="Overtime hours recorded from the attendance import since the first of the month, across every branch, after any override HR has made to a day."
        icon={<Timer className="size-4" />}
        trend={
          overtimeWeeks.length > 1
            ? {
                values: overtimeWeeks,
                colour: "ochre" as const,
                label: "Overtime by week",
                format: (value: number) => `${value.toFixed(1)} h`,
              }
            : undefined
        }
        caption="Across every branch."
      />
      <FigureTile
        label="Headcount, 30 days"
        value={change > 0 ? `+${change}` : String(change)}
        unit="staff"
        help="People who joined in the last thirty days, less people whose last working day fell in them. A minus number means the company got smaller."
        accent={change > 0 ? "go" : change < 0 ? "alert" : undefined}
        icon={rising ? <TrendingUp className="size-4" /> : <TrendingDown className="size-4" />}
        caption="Joiners less leavers."
      />
    </div>
  );
}

/**
 * Everything waiting on HR, as an inbox.
 *
 * The same list of queues it always was. The inbox itself, with its tabs and
 * its clearing, is shared with the employee home, so the two behave alike;
 * what is HR's own is the wording, and where the cleared rows are kept.
 */
function TaskList({ tasks, menu }: { tasks: PendingTask[]; menu: ReactNode }) {
  return (
    <Inbox
      items={tasks}
      storageKey="hrsys.inbox.cleared"
      grip
      actions={menu}
      describe={(live) =>
        // "4 to deal with" above the numbers 76, 2, 183 and 4 reads as a
        // contradiction. It was counting kinds of thing, not things.
        `${live === 1 ? "One queue" : `${live} queues`} with something in. Open one to deal with it, or clear it to put it aside.`
      }
      help={
        <>
          <p>
            Each row is a queue: leave requests, letters, profiles to review, and so on. The
            number is how many things are waiting in it right now.
          </p>
          <p>
            Red needs doing today and is under Important. Blue is ordinary work and grey can
            wait; both are under Other. Only queues with something in them are listed.
          </p>
          <p>
            Clearing a row puts it aside for you alone, on this computer. It comes back by
            itself when its number changes, and it is under Cleared until then.
          </p>
        </>
      }
      empty={
        <Card flush>
          <EmptyState
            icon={<CheckCheck className="size-5" />}
            title="Nothing is waiting on you"
            description="Approvals, expiring papers and probation dates appear here as they come up."
          />
        </Card>
      }
    />
  );
}

/**
 * Fourteen days of cover, per branch, as a strip.
 *
 * The endpoint returns the exact headcount against the minimum for every
 * branch on every one of the next fourteen days. The screen used to join that
 * into a sentence with middle dots, which is a lot of precision spent on
 * something nobody could read at a glance. A row of cells says the same thing
 * in the shape of the week: where the gaps are, whether they cluster, and
 * whether one branch is carrying all of them.
 */
function ShortStrip({ days }: { days: UnderstaffedDay[] }) {
  // The response only carries days that are short, so the fortnight has to be
  // rebuilt around them to show the days that are fine.
  const today = new Date();
  const dates = Array.from({ length: 14 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + index);
    return date.toISOString().slice(0, 10);
  });

  const branches = new Map<string, { name: string; short: Map<string, number> }>();
  for (const day of days) {
    for (const branch of day.branches) {
      const held =
        branches.get(branch.branch_id) ??
        { name: branch.branch_name, short: new Map<string, number>() };
      held.short.set(day.date, branch.min_headcount - branch.headcount);
      branches.set(branch.branch_id, held);
    }
  }

  return (
    <div className="space-y-3">
      {[...branches.entries()].map(([id, branch]) => (
        <div key={id}>
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate text-base font-medium text-fg">{branch.name}</p>
            <span className="u-label shrink-0">
              {branch.short.size} of 14 days short
            </span>
          </div>
          <ol className="mt-1.5 flex gap-1" aria-label={`${branch.name}, next fourteen days`}>
            {dates.map((date) => {
              const short = branch.short.get(date) ?? 0;
              return (
                <li
                  key={date}
                  title={
                    short > 0
                      ? `${formatDate(date)}: ${short} short`
                      : `${formatDate(date)}: covered`
                  }
                  className={cn(
                    "h-6 flex-1 rounded-xs",
                    short === 0
                      ? "bg-ink-100"
                      : short === 1
                        ? "bg-ochre-100"
                        : "bg-alert-100",
                  )}
                />
              );
            })}
          </ol>
        </div>
      ))}
      <Link
        to="/admin/settings?section=cover"
        className="inline-block text-xs font-semibold text-accent hover:underline"
      >
        Arrange cover
      </Link>
    </div>
  );
}
